import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { EMPTY, Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { CustomerCheckoutService } from '../../../../customer/customer-checkout/customer-checkout.service';
import { CustomerKbaConversation, MessageService, SsoAccountFacade, SsoAccountData, IdentityVerificationStatus, CustomerKbaAnswerData, IdentityVerificationResultStateHelper } from 'common';
import { catchError, map, mergeMap, take } from 'rxjs/operators';
import { VerifyIdentityFacade } from '../verify-identity.facade';

@Component({
    selector: 'ifp-kba-form-content',
    templateUrl: './kba-form-content.component.html',
    styleUrls: ['./kba-form-content.component.scss'],
    standalone: false
})
export class KbaFormContentComponent implements OnInit {
  kbaConversation: CustomerKbaConversation;
  form: UntypedFormGroup;
  submitEnabled = true;
  private submitSub: Subscription;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private customerCheckoutService: CustomerCheckoutService,
    private messageService: MessageService,
    private ssoAccountFacade: SsoAccountFacade,
    private verifyIdentityFacade: VerifyIdentityFacade,
  ) { }

  ngOnInit(): void {
    this.initData();
  }

  private initData(): void {
    this.customerCheckoutService.getQuestions()
      .pipe(catchError((error) => {
        this.messageService.error(error);
        return EMPTY;
      }))
      .subscribe((conversation: CustomerKbaConversation) => {
        this.kbaConversation = conversation;
        this.formInit();
      });
  }

  private formInit(): void {
    this.form = this.formBuilder.group({
      conversationId: [this.kbaConversation.conversationId, Validators.required],
      answers: this.formBuilder.group({
        QuestionSetId: [this.kbaConversation.questionSet.QuestionSetId, Validators.required],
        Questions: this.formBuilder.array(this.kbaConversation.questionSet.Questions.map(question => this.formBuilder.group({
          QuestionId: question.QuestionId,
          Choices: this.formBuilder.array([
            this.formBuilder.group({ Choice: ['', Validators.required] })
          ])
        })))
      })
    });
  }

  submit(): void {
    if (!this.form.valid)
      return;

    this.ssoAccountFacade.clearSsoAccountCache();

    this.submitSub = this.customerCheckoutService
      .answer(this.form.value)
      .pipe(mergeMap(results => {
        return this.ssoAccountFacade.getCurrentSsoAccount$().pipe(take(1), map(currentSsoAccount => {
          return { results, currentSsoAccount };
        }))
      }))
      .subscribe({
        next: (results) => this.submitSuccess(results.results, results.currentSsoAccount),
        error: this.submitError.bind(this)
      });
  }

  private submitError(response: any): void {
    this.submitSubClear();
    if (response.status === 400) {
      const errorMessage = response.error?.error_description || response.error;
      this.submitEnabled = false;
      IdentityVerificationResultStateHelper.set({ message: errorMessage });
      this.router.navigate(['account', 'setup', 'verify-identity-result'], { 
        replaceUrl: true,
      });
    } else {
      this.messageService.error(response);
    }
  }

  private submitSuccess(results: CustomerKbaAnswerData, currentAccount: SsoAccountData): void {
    this.submitSubClear();
    if (results.status === IdentityVerificationStatus.Success) {
      this.submitEnabled = false;
      this.verifyIdentityFacade.handleVerifyIdentitySuccessRedirect(currentAccount);
    } else if (results.status === IdentityVerificationStatus.Pending) {
      this.kbaConversation = results.questions;
      this.formInit();
    }
  }

  private submitSubClear(): void {
    this.submitSub.unsubscribe();
    this.submitSub = null;
  }
}
